import { useEffect, useMemo, useRef, useState } from "react";

import type { OrgPerson } from "@/lib/api";
import { type ChangeIndex, childrenIndex, layout, NODE_H, NODE_W, type OrgMap } from "@/lib/org";

/**
 * The reporting tree as SVG, drawn with design-system colour utilities so it
 * follows light and dark like the rest of the app.
 *
 * `before` and `after` are the same people; `mode` picks which lines are
 * drawn. In "after" a person whose manager changes sits on the new branch and
 * says where they came from; in "before" they sit on the old one and say where
 * they are going. Title and department changes are marked either way.
 */

export type ChartMode = "before" | "after";

const MAX_CHARS = 32;

// Zoom limits: below ~30% the node text is gone and the chart is a diagram of
// blocks, which is still useful for seeing the shape of a 2 000-person tree;
// above 2x there is nothing more to see.
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.2;
const WHEEL_STEP = 0.12;
/** Pixels of travel before a press becomes a pan rather than a click. */
const DRAG_THRESHOLD = 4;

function clip(text: string | null | undefined): string {
  const t = text ?? "";
  return t.length > MAX_CHARS ? `${t.slice(0, MAX_CHARS - 1)}…` : t;
}

export function OrgChart({
  before,
  after,
  mode,
  rootId,
  changes,
  issueLevel,
  showAccounts,
  initiallyOpen,
  selected,
  onSelect,
}: {
  before: OrgMap;
  after: OrgMap;
  mode: ChartMode;
  rootId: string;
  changes: ChangeIndex;
  /** Worst issue per person: drives the red outline. */
  issueLevel: Map<string, "error" | "warning" | "info">;
  /** Show mailboxes, scanners and admin accounts too. */
  showAccounts: boolean;
  /** Which nodes start expanded. Everything else starts collapsed. */
  initiallyOpen: (id: string, depth: number) => boolean;
  selected: string | null;
  onSelect: (id: string) => void;
}) {
  const map = mode === "after" ? after : before;
  const other = mode === "after" ? before : after;
  const children = useMemo(
    () =>
      childrenIndex(
        map,
        (p) =>
          (showAccounts || p.person !== false) && (p.account_enabled !== false || p.id === rootId),
      ),
    [map, showAccounts, rootId],
  );
  const [toggled, setToggled] = useState<Set<string>>(new Set());

  // Collapsed = the default for this node, flipped if the user clicked it.
  const collapsed = useMemo(() => {
    const out = new Set<string>();
    const walk = (id: string, depth: number, seen: Set<string>) => {
      if (seen.has(id)) {
        return;
      }
      seen.add(id);
      const open = initiallyOpen(id, depth) !== toggled.has(id);
      if (!open) {
        out.add(id);
      }
      for (const k of children.get(id) ?? []) {
        walk(k.id, depth + 1, seen);
      }
    };
    walk(rootId, 0, new Set());
    return out;
  }, [children, rootId, initiallyOpen, toggled]);

  const laid = useMemo(() => layout(rootId, children, collapsed), [rootId, children, collapsed]);
  const pos = new Map(laid.nodes.map((n) => [n.id, n]));

  // A wide team puts the root far down the canvas; start with it in view.
  const box = useRef<HTMLDivElement>(null);
  const rootY = pos.get(rootId)?.y ?? 0;
  const [scale, setScale] = useState(1);
  useEffect(() => {
    if (box.current) {
      box.current.scrollTop = Math.max(0, rootY * scale - box.current.clientHeight / 3);
    }
    // Only when the root moves: re-running this on every zoom would fight the
    // scroll position zoomAt() has just calculated.
  }, [rootId, rootY]);

  const canvasW = laid.width + 32;
  const canvasH = laid.height + 24;

  /**
   * Zoom while holding one point still.
   *
   * The canvas is scaled, not re-laid out, so the scroll offsets have to move
   * with it: the content coordinate under `(cx, cy)` — measured from the
   * viewport's top-left — is `(scroll + offset) / scale`, and it must land on
   * the same pixel afterwards. Without this the chart lurches away from
   * whatever the person was looking at, which on a 2 000-person tree means
   * losing your place entirely.
   */
  const zoomAt = (next: number, cx?: number, cy?: number) => {
    const el = box.current;
    const to = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
    if (!el) {
      setScale(to);
      return;
    }
    const ox = cx ?? el.clientWidth / 2;
    const oy = cy ?? el.clientHeight / 2;
    const contentX = (el.scrollLeft + ox) / scale;
    const contentY = (el.scrollTop + oy) / scale;
    setScale(to);
    // After React has resized the canvas, not before: scrollLeft is clamped to
    // the current scroll width, so setting it on a canvas that is still the
    // old size silently truncates when zooming in.
    requestAnimationFrame(() => {
      el.scrollLeft = contentX * to - ox;
      el.scrollTop = contentY * to - oy;
    });
  };

  /** Scale so the whole tree fits the viewport, never magnifying past 1:1. */
  const fit = () => {
    const el = box.current;
    if (!el) {
      return;
    }
    const next = Math.min(el.clientWidth / canvasW, el.clientHeight / canvasH, 1);
    setScale(Math.max(MIN_ZOOM, next));
    requestAnimationFrame(() => {
      el.scrollTop = 0;
      el.scrollLeft = 0;
    });
  };

  // Drag anywhere on the background to pan. A node's own click handler still
  // wins: panning only starts once the pointer has actually travelled, so a
  // click that does not move is a click.
  const drag = useRef<{ x: number; y: number; left: number; top: number; live: boolean } | null>(
    null,
  );
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Left button only, and never from a form control inside a node.
    if (e.button !== 0 || !box.current) {
      return;
    }
    drag.current = {
      x: e.clientX,
      y: e.clientY,
      left: box.current.scrollLeft,
      top: box.current.scrollTop,
      live: false,
    };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    const el = box.current;
    if (!d || !el) {
      return;
    }
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.live && Math.hypot(dx, dy) < DRAG_THRESHOLD) {
      return;
    }
    if (!d.live) {
      d.live = true;
      el.setPointerCapture(e.pointerId);
    }
    el.scrollLeft = d.left - dx;
    el.scrollTop = d.top - dy;
  };
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (drag.current?.live && box.current?.hasPointerCapture(e.pointerId)) {
      box.current.releasePointerCapture(e.pointerId);
    }
    drag.current = null;
  };

  const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    // Plain wheel keeps scrolling the canvas, which is what a trackpad user
    // expects; ctrl/⌘ is the pinch gesture the browser reports and the usual
    // zoom modifier everywhere else.
    if (!e.ctrlKey && !e.metaKey) {
      return;
    }
    e.preventDefault();
    const rect = box.current?.getBoundingClientRect();
    zoomAt(
      scale * (e.deltaY < 0 ? 1 + WHEEL_STEP : 1 - WHEEL_STEP),
      rect ? e.clientX - rect.left : undefined,
      rect ? e.clientY - rect.top : undefined,
    );
  };

  // The wheel listener has to be non-passive to call preventDefault, which
  // React's onWheel cannot guarantee; attach it directly.
  useEffect(() => {
    const el = box.current;
    if (!el) {
      return;
    }
    const stop = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
      }
    };
    el.addEventListener("wheel", stop, { passive: false });
    return () => el.removeEventListener("wheel", stop);
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "+" || e.key === "=") {
      e.preventDefault();
      zoomAt(scale + ZOOM_STEP);
    } else if (e.key === "-" || e.key === "_") {
      e.preventDefault();
      zoomAt(scale - ZOOM_STEP);
    } else if (e.key === "0") {
      e.preventDefault();
      zoomAt(1);
    } else if (e.key.toLowerCase() === "f") {
      e.preventDefault();
      fit();
    }
  };

  const toggle = (id: string) =>
    setToggled((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  return (
    <div className="relative mt-3">
      <ZoomBar
        scale={scale}
        onIn={() => zoomAt(scale + ZOOM_STEP)}
        onOut={() => zoomAt(scale - ZOOM_STEP)}
        onReset={() => zoomAt(1)}
        onFit={fit}
      />
      <div
        ref={box}
        tabIndex={0}
        role="application"
        aria-label="Reporting structure. Drag to pan, plus and minus to zoom, F to fit."
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onWheel={onWheel}
        onKeyDown={onKeyDown}
        className="border-border bg-bg focus-visible:ring-accent h-[70vh] cursor-grab overflow-auto rounded border focus:outline-none focus-visible:ring-2 active:cursor-grabbing"
      >
        <svg
          width={canvasW * scale}
          height={canvasH * scale}
          viewBox={`0 0 ${canvasW} ${canvasH}`}
          role="img"
          aria-label="Reporting structure"
          className="block"
        >
          <g transform="translate(12,12)">
            {laid.links.map((l) => {
              const a = pos.get(l.from)!;
              const b = pos.get(l.to)!;
              const x1 = a.x + NODE_W;
              const y1 = a.y + NODE_H / 2;
              const x2 = b.x;
              const y2 = b.y + NODE_H / 2;
              const mx = (x1 + x2) / 2;
              const moved = changes.get(l.to)?.has("manager");
              return (
                <path
                  key={`${l.from}-${l.to}`}
                  d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`}
                  className={moved ? "stroke-accent" : "stroke-border-strong"}
                  strokeWidth={moved ? 2 : 1}
                  fill="none"
                />
              );
            })}
            {laid.nodes.map((n) => {
              const p = map.get(n.id)!;
              return (
                <Node
                  key={n.id}
                  person={p}
                  x={n.x}
                  y={n.y}
                  mode={mode}
                  changed={changes.get(n.id)}
                  otherManager={other.get(other.get(n.id)?.manager_id ?? "")}
                  issue={issueLevel.get(n.id)}
                  childCount={n.childCount}
                  collapsed={n.collapsed}
                  selected={selected === n.id}
                  onSelect={() => onSelect(n.id)}
                  onToggle={() => toggle(n.id)}
                />
              );
            })}
          </g>
        </svg>
      </div>
    </div>
  );
}

/** Zoom controls, floating over the top-right of the canvas. */
function ZoomBar({
  scale,
  onIn,
  onOut,
  onReset,
  onFit,
}: {
  scale: number;
  onIn: () => void;
  onOut: () => void;
  onReset: () => void;
  onFit: () => void;
}) {
  const btn =
    "text-fg-muted hover:text-fg hover:bg-bg-subtle disabled:opacity-40 px-2 py-1 text-xs";
  return (
    <div className="border-border bg-bg absolute top-2 right-2 z-10 flex items-center rounded border shadow-sm">
      <button className={btn} onClick={onOut} disabled={scale <= MIN_ZOOM} aria-label="Zoom out">
        −
      </button>
      <button
        className={`${btn} tabular-nums`}
        onClick={onReset}
        aria-label="Reset zoom to 100 percent"
        title="Reset to 100% (0)"
      >
        {Math.round(scale * 100)}%
      </button>
      <button className={btn} onClick={onIn} disabled={scale >= MAX_ZOOM} aria-label="Zoom in">
        +
      </button>
      <span className="bg-border h-4 w-px" />
      <button className={btn} onClick={onFit} title="Fit the whole chart (F)">
        Fit
      </button>
    </div>
  );
}

function Node({
  person,
  x,
  y,
  mode,
  changed,
  otherManager,
  issue,
  childCount,
  collapsed,
  selected,
  onSelect,
  onToggle,
}: {
  person: OrgPerson;
  x: number;
  y: number;
  mode: ChartMode;
  changed: Map<string, unknown> | undefined;
  otherManager: OrgPerson | undefined;
  issue: "error" | "warning" | "info" | undefined;
  childCount: number;
  collapsed: boolean;
  selected: boolean;
  onSelect: () => void;
  onToggle: () => void;
}) {
  const moved = changed?.has("manager");
  const attr = changed && [...changed.keys()].some((k) => k !== "manager");
  const stroke =
    issue === "error"
      ? "stroke-danger"
      : moved
        ? "stroke-accent"
        : attr
          ? "stroke-warning"
          : selected
            ? "stroke-fg"
            : "stroke-border";
  const fill = selected ? "fill-bg-muted" : moved || attr ? "fill-accent-subtle" : "fill-bg-subtle";
  const sub = moved
    ? `${mode === "after" ? "from" : "to"} ${otherManager?.display_name ?? "nobody"}`
    : person.job_title;
  return (
    <g transform={`translate(${x},${y})`}>
      <g onClick={onSelect} className="cursor-pointer">
        <title>
          {[person.display_name, person.job_title, person.department, person.upn]
            .filter(Boolean)
            .join("\n")}
        </title>
        <rect
          width={NODE_W}
          height={NODE_H}
          rx={6}
          className={`${fill} ${stroke}`}
          strokeWidth={moved || attr || issue === "error" || selected ? 2 : 1}
          strokeDasharray={person.account_enabled ? undefined : "4 3"}
        />
        <text
          x={10}
          y={18}
          className={`text-[12px] font-semibold ${person.person ? "fill-fg" : "fill-fg-muted italic"}`}
        >
          {clip(person.display_name)}
          {person.account_enabled ? "" : " (disabled)"}
        </text>
        <text
          x={10}
          y={34}
          className={`text-[11px] ${moved ? "fill-fg font-medium" : "fill-fg-muted"}`}
        >
          {clip(sub)}
        </text>
        {attr ? (
          <circle cx={NODE_W - 10} cy={10} r={4} className="fill-warning">
            <title>Title, department or another attribute changes</title>
          </circle>
        ) : null}
      </g>
      {childCount > 0 ? (
        <g
          onClick={onToggle}
          className="cursor-pointer"
          transform={`translate(${NODE_W},${NODE_H / 2})`}
        >
          <circle r={10} className="fill-bg stroke-border-strong" strokeWidth={1} />
          <text textAnchor="middle" y={4} className="fill-fg-muted text-[10px]">
            {collapsed ? `+${childCount}` : "−"}
          </text>
        </g>
      ) : null}
    </g>
  );
}
